import filter from "lodash/filter";
import omit from "lodash/omit";
import { getPostUntilChangeReason } from "PFApp/components/expiry_date_modal";
import {
  FIELD_ASSIGNEE_ID_KEY,
  FIELD_ATTACHMENT_IDS_KEY,
  FIELD_AUDIENCES_KEY,
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_COMMUNICATION_TYPE_KEY,
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_DESCRIPTION_KEY,
  FIELD_DEVELOPMENTAL_KEY,
  FIELD_INTEREST_QUESTIONS_KEY,
  FIELD_INTEREST_QUESTIONS_TOGGLE_KEY,
  FIELD_INTEREST_STATEMENT_ENABLED_KEY,
  FIELD_METADATA_KEY,
  FIELD_MIN_REQUIRED_COUNT_KEY,
  FIELD_NAME_KEY,
  FIELD_PARENT_ACTIVITY_KEY,
  FIELD_POST_UNTIL_CHANGE_REASON_KEY,
  FIELD_POST_UNTIL_KEY,
  FIELD_PRIVACY_KEY,
  FIELD_SKILLS_FRAMEWORK_IDS_KEY,
  FIELD_SUBTEMPLATE_KEY,
  FIELD_TEMPLATE_ID_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { convertAPICustomFieldsToFormValues } from "PFApp/helpers/activity_form/data_conversions";
import { getPrivacyConfiguration } from "PFApp/helpers/activity_form/get_privacy_configuration";
import { useInterestQuestionariesTemplateConfiguration } from "PFApp/hooks";
import { useInitialQuestionsValues } from "PFApp/hooks/activity_form/use_initial_questions_values";
import { ActivityFormType } from "PFApp/types/activity_form/activity_form_schema";
import { activityCustomTypesAccessLevels } from "PFCore/helpers/custom_types";
import { isProfileActive } from "PFCore/helpers/profile";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { AccessLevel } from "PFTypes";
import { PropsWithChildren } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useActivityFormStore } from "./activity_form_store";

export const ActivityFormProvider = ({ children }: PropsWithChildren) => {
  const activity = useActivityFormStore((state) => state.activity);
  const template = useActivityFormStore((state) => state.template);
  const attachments = useActivityFormStore((state) => state.attachments);
  const templateConfig = useInterestQuestionariesTemplateConfiguration(template);
  const { isClone, isEdit } = useActivityFormStore((state) => state.activityStatus);
  const parentActivity = useActivityFormStore((state) => state.parentActivity);
  const { t } = useTranslation("activities", { keyPrefix: "form" });
  const { data: currentProfile } = useCurrentProfile();

  const { getInitialQuestionsValues } = useInitialQuestionsValues({ template });
  const { initialQuestionsValues, initialQuestionsToggleValue } = getInitialQuestionsValues({
    inheritFromActivity: isEdit,
    isClone,
    activity
  });
  const { isDefaultPrivate } = getPrivacyConfiguration({ template, parentActivity });

  const defaultPostUntilChangeReason = getPostUntilChangeReason(
    activity.post_until_change_reason ?? null,
    template.localized_post_until_change_reasons ?? []
  );

  // Filter custom fields when cloning to only include fields with write permission
  const editableCustomFields = isClone
    ? filter(
        activity.custom_fields,
        (customField) =>
          activityCustomTypesAccessLevels(currentProfile, null)[customField.type.id] === AccessLevel.ReadWrite
      )
    : activity.custom_fields;

  const methods = useForm<ActivityFormType>({
    reValidateMode: "onSubmit",
    defaultValues: {
      [FIELD_TEMPLATE_ID_KEY]: template.id,
      [FIELD_SUBTEMPLATE_KEY]: activity.subtemplate_key,
      [FIELD_NAME_KEY]: isClone
        ? t("sections.basicInformation.clone", { activityName: activity.name })
        : activity.name || "",
      [FIELD_DESCRIPTION_KEY]: activity.description || "",
      [FIELD_ASSIGNEE_ID_KEY]: isProfileActive(activity.assignee?.profile)
        ? activity?.assignee?.profile?.id || null
        : null,
      [FIELD_PARENT_ACTIVITY_KEY]: parentActivity || null,
      [FIELD_MIN_REQUIRED_COUNT_KEY]: activity.min_required_count || 1,
      [FIELD_ATTACHMENT_IDS_KEY]: attachments.map(({ id }) => id),
      [FIELD_INTEREST_STATEMENT_ENABLED_KEY]: !!templateConfig?.showStatement,
      [FIELD_INTEREST_QUESTIONS_KEY]: initialQuestionsValues,
      [FIELD_INTEREST_QUESTIONS_TOGGLE_KEY]: initialQuestionsToggleValue, // do not submit in form
      [FIELD_PRIVACY_KEY]: isClone || isEdit ? !!activity.private : isDefaultPrivate,
      [FIELD_AUDIENCES_KEY]: activity.audiences || [],
      [FIELD_COMMUNICATION_TYPE_KEY]: activity.communication_type || "direct",
      [FIELD_CUSTOM_FIELDS_KEY]: convertAPICustomFieldsToFormValues(editableCustomFields),
      [FIELD_POST_UNTIL_KEY]: activity.post_until,
      [FIELD_POST_UNTIL_CHANGE_REASON_KEY]: defaultPostUntilChangeReason,
      [FIELD_DEVELOPMENTAL_KEY]: activity.developmental || false,
      [FIELD_SKILLS_FRAMEWORK_IDS_KEY]: activity.skills_framework_ids || [],
      [FIELD_BOOKING_CATEGORY_ID_KEY]: activity.booking_category_id ?? undefined,
      [FIELD_METADATA_KEY]: {
        availability: activity.metadata.availability || {},
        ...omit(activity.metadata, ["interest_questions", "interest_statement_enabled"])
      }
    }
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};
